import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, ChevronRight, Eye, EyeOff, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { kindDetailQuery, meaningsQuery, resolveMeanings, suggestKind, useKindMutations, type MeaningDef } from '@/api/vocab'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, Input } from '@/components/ui/input'
import { cn } from '@/components/ui/utils'
import { KindEditorDialog } from '@/features/vocabulary/KindEditorDialog'
import { enterScope } from '@/lib/keys'
import { suggestColor, useVocabStore } from '@/stores/vocabStore'
import type { FieldDef, KindDef } from '@/schema'

import { KindIcon, iconNames, lucideIcon } from './KindPicker'
import { describeKind, DOMAIN_WORDS, useVocab, type StageDomain } from './vocab'

/**
 * "+ New kind" and "Edit this kind" in plain words, for all four stage lists (shapes, spots, ways
 * in and out, paths).
 *
 * A name is all it takes: the studio suggests a colour, an icon and what the name probably means
 * (`POST /api/vocab/suggest`), and the "What does it do?" choices map onto the engine's semantics
 * on the server (`/api/vocab/meanings`), so nobody sees a role or a z-order. "Extra things to fill
 * in" become per-kind fields, shown in Details for every shape of this kind and stored in its
 * `props`. Editing a kind also offers hiding it from the lists (it keeps working where it is used).
 */

export interface NewKindDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  domain: StageDomain
  /** Edit this kind; absent = make a new one. */
  kindId?: string
  /** Pre-filled name for a new kind (what was typed in the search). */
  initialName?: string
  /** The new or saved kind. */
  onDone?(kind: KindDef): void
  /** Where the focus goes when it closes (default: Radix's, which is <body> when nothing opened it). */
  onCloseAutoFocus?(event: Event): void
}

type FieldType = 'text' | 'number' | 'boolean' | 'select' | 'asset_ref'

interface FieldDraft {
  /** Kept for fields that already exist, so saved values still match. */
  key: string
  label: string
  type: FieldType
  /** Comma-separated choices for `select`. */
  options: string
}

const TYPE_LABEL: Record<FieldType, string> = {
  text: 'Words',
  number: 'A number',
  boolean: 'Yes or no',
  select: 'Pick from a list',
  asset_ref: 'A picture from the library',
}

const SWATCH_HUES = [25, 55, 85, 130, 160, 195, 230, 265, 300, 335]
const SWATCHES = SWATCH_HUES.map((h) => `oklch(0.72 0.15 ${h})`)

/** Icons worth offering before anyone searches, per list. */
const SUGGESTED_ICONS: Record<StageDomain, string[]> = {
  region_kind: ['footprints', 'waves', 'cloud', 'mountain', 'trees', 'tree-pine', 'flower-2', 'leaf', 'flame', 'snowflake', 'droplets', 'sun', 'ban', 'octagon-x', 'square', 'brick-wall', 'fence', 'layers', 'eye-off', 'layout-panel-top', 'house', 'castle', 'tent', 'sailboat', 'sparkles', 'shapes'],
  anchor_kind: ['door-open', 'bed', 'armchair', 'sofa', 'table', 'lamp', 'tree-pine', 'shrub', 'mountain', 'flame', 'tent', 'ladder', 'chevrons-up', 'signpost', 'mailbox', 'gem', 'crown', 'cooking-pot', 'baby', 'egg', 'star', 'map-pin', 'eye', 'package', 'utensils', 'bath'],
  portal_kind: ['door-open', 'move-horizontal', 'route', 'chevrons-up', 'waves', 'sparkles', 'arrow-right', 'arrow-left', 'log-out', 'log-in', 'rocket', 'train-front'],
  path_kind: ['footprints', 'bird', 'waves', 'move-up', 'route', 'spline', 'rabbit', 'fish', 'plane', 'snail'],
}

/** Label → a field key the server accepts (`^[a-z][a-z0-9_]{0,39}$`), unique among `taken`. */
export function fieldKeyFromLabel(label: string, taken: ReadonlySet<string>): string {
  let base = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 34)
  if (!base) base = 'field'
  if (!/^[a-z]/.test(base)) base = `f_${base}`
  let key = base
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`
  return key
}

/** Drafts → `FieldDef[]` (keys minted for new rows, options split and de-duplicated). */
export function fieldsFromDrafts(drafts: readonly FieldDraft[]): { fields: FieldDef[]; error: string | null } {
  const taken = new Set(drafts.map((d) => d.key).filter(Boolean))
  const fields: FieldDef[] = []
  for (const d of drafts) {
    const label = d.label.trim()
    if (!label) return { fields: [], error: 'Give every extra thing a name, or remove it.' }
    const key = d.key || fieldKeyFromLabel(label, taken)
    taken.add(key)
    const f: FieldDef = { key, label, type: d.type }
    if (d.type === 'select') {
      const seen = new Set<string>()
      const options = d.options
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean)
        .map((o) => ({ value: fieldKeyFromLabel(o, new Set()).replace(/^f_/, ''), label: o }))
        .filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)))
      if (options.length === 0) return { fields: [], error: `“${label}” needs at least one choice (separate them with commas).` }
      f.options = options
    }
    fields.push(f)
  }
  return { fields, error: null }
}

function draftsFromFields(fields: readonly FieldDef[]): FieldDraft[] {
  return fields.map((f) => ({ key: f.key, label: f.label || f.key, type: f.type, options: (f.options ?? []).map((o) => o.label).join(', ') }))
}

/** Toggle `id` respecting the exclusive groups: one of `front`, one of `how`, and `start` alone. */
export function toggleMeaning(chosen: readonly string[], id: string, catalogue: readonly MeaningDef[]): string[] {
  if (chosen.includes(id)) return chosen.filter((x) => x !== id)
  const def = catalogue.find((m) => m.id === id)
  if (!def) return [...chosen, id]
  if (def.group === 'start') return [id]
  const startIds = new Set(catalogue.filter((m) => m.group === 'start').map((m) => m.id))
  const sameGroup = def.group ? new Set(catalogue.filter((m) => m.group === def.group).map((m) => m.id)) : new Set<string>()
  return [...chosen.filter((x) => !sameGroup.has(x) && !startIds.has(x)), id]
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function NewKindDialog({ open, onOpenChange, domain, kindId, initialName, onDone, onCloseAutoFocus }: NewKindDialogProps) {
  const vocab = useVocab()
  const store = useVocabStore()
  const { quick, patch } = useKindMutations()
  const editing = kindId !== undefined
  const words = DOMAIN_WORDS[domain]
  const kind = editing ? vocab.get(kindId, domain) : undefined

  useEffect(() => (open ? enterScope('dialog') : undefined), [open])

  const catalogueQ = useQuery({ ...meaningsQuery(domain), enabled: open })
  const catalogue = catalogueQ.data?.domains[domain]
  const detailQ = useQuery({ ...kindDetailQuery(domain, kindId ?? ''), enabled: open && editing })

  const [name, setName] = useState(initialName ?? '')
  const [color, setColor] = useState<string | null>(null)
  const [icon, setIcon] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string[] | null>(null)
  const [description, setDescription] = useState('')
  const [drafts, setDrafts] = useState<FieldDraft[]>([])
  const [moreOpen, setMoreOpen] = useState(false)
  const [iconsOpen, setIconsOpen] = useState(false)
  const [iconQuery, setIconQuery] = useState('')
  const [confirmHide, setConfirmHide] = useState(false)
  const [fullEditor, setFullEditor] = useState(false)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  // Editing: start from the kind as it is (once per kind).
  useEffect(() => {
    if (!editing || !kind || loadedFor === kind.id) return
    setName(kind.label['en'] ?? kind.id)
    setColor(kind.color)
    setIcon(kind.icon ?? null)
    setDescription(kind.description ?? '')
    setDrafts(draftsFromFields(kind.fields ?? []))
    setMoreOpen((kind.fields ?? []).length > 0)
    setLoadedFor(kind.id)
  }, [editing, kind, loadedFor])

  const detailMeanings = detailQ.data?.meanings
  const debouncedName = useDebounced(name.trim(), 350)
  const suggestionQ = useQuery({
    queryKey: ['vocab-suggest', domain, debouncedName],
    queryFn: ({ signal }) => suggestKind({ domain, name: debouncedName }, signal),
    enabled: open && !editing && debouncedName.length > 0,
    staleTime: 60_000,
    retry: false,
    placeholderData: (prev) => prev,
  })
  const suggestion = editing || !name.trim() ? undefined : suggestionQ.data

  const baseMeanings = editing ? (detailMeanings ?? []) : (suggestion?.meanings ?? [])
  const meanings = chosen ?? baseMeanings
  const effectiveColor = color ?? suggestion?.color ?? suggestColor(store, domain, `x:${name.trim() || 'new'}`)
  const effectiveIcon = icon ?? suggestion?.icon ?? catalogue?.default_icon ?? null

  const likeKind = !editing && suggestion?.like ? vocab.get(suggestion.like, domain) : undefined
  const baseKind = editing ? kind : likeKind
  const base = baseKind ? (baseKind.semantics as unknown as Record<string, unknown>) : undefined
  const meaningsKey = [...meanings].sort().join(',')
  const summaryQ = useQuery({
    queryKey: ['vocab-resolve', domain, meaningsKey, baseKind?.id ?? ''],
    queryFn: ({ signal }) => resolveMeanings({ domain, meanings, ...(base ? { base } : {}) }, signal),
    enabled: open && Boolean(catalogue) && (chosen !== null || (editing ? detailMeanings !== undefined : Boolean(suggestion))),
    staleTime: 60_000,
    retry: false,
    placeholderData: (prev) => prev,
  })
  const summary = summaryQ.data?.summary ?? (editing && chosen === null ? describeKind(kind) : suggestion?.summary ?? [])

  const fieldsResult = useMemo(() => fieldsFromDrafts(drafts), [drafts])
  const nameError = name.trim() ? null : `Give the ${words.one} a name.`
  const existing = !editing && suggestion?.existing ? vocab.get(suggestion.existing, domain) : undefined
  const pending = quick.isPending || patch.isPending

  const shownIcons = useMemo(() => {
    const q = iconQuery.trim().toLowerCase()
    if (!q) return SUGGESTED_ICONS[domain].filter((n) => lucideIcon(n))
    return iconNames()
      .filter((n) => n.includes(q.replace(/\s+/g, '-')))
      .slice(0, 60)
  }, [iconQuery, domain])

  const save = () => {
    if (nameError || fieldsResult.error) return
    if (editing && kind) {
      const body: Parameters<typeof patch.mutate>[0]['body'] = {
        label: { ...kind.label, en: name.trim() },
        color: effectiveColor,
        ...(effectiveIcon ? { icon: effectiveIcon } : {}),
        description: description.trim() || undefined,
        fields: fieldsResult.fields,
      }
      if (chosen !== null && [...chosen].sort().join(',') !== [...(detailMeanings ?? [])].sort().join(',')) body.meanings = chosen
      patch.mutate({ domain, id: kind.id, body }, { onSuccess: (k) => onDone?.(k) })
      return
    }
    // The suggestion lags the name by a debounce: one for an older name (typed "lava", then
    // "river" and Enter at once) must not decide what the new kind does — the server guesses
    // from the name instead, and choices the person ticked still count.
    const fresh = suggestion !== undefined && (suggestion.label['en'] ?? '').trim() === name.trim()
    quick.mutate(
      {
        domain,
        name: name.trim(),
        ...(chosen !== null ? { meanings: chosen } : fresh ? { meanings } : {}),
        ...(fresh && suggestion.like ? { like: suggestion.like } : {}),
        color: effectiveColor,
        ...(effectiveIcon ? { icon: effectiveIcon } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(fieldsResult.fields.length ? { fields: fieldsResult.fields } : {}),
      },
      { onSuccess: (k) => onDone?.(k) },
    )
  }

  const setHidden = (hidden: boolean) => {
    if (!kind) return
    patch.mutate({ domain, id: kind.id, body: { enabled: !hidden } }, { onSuccess: (k) => (hidden ? onDone?.(k) : undefined) })
    setConfirmHide(false)
  }

  const catalogueMeanings = catalogue?.meanings ?? []

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="lg" className="max-h-[min(92dvh,900px)]" data-testid="new-kind-dialog" onCloseAutoFocus={onCloseAutoFocus}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit “${kind?.label['en'] ?? kindId}”` : `New ${words.one}`}</DialogTitle>
            <DialogDescription>
              {editing
                ? `Change how this ${words.one} looks and what it does in stories.`
                : `Name it and say what it does in stories. You can use it straight away, on this picture and every other.`}
            </DialogDescription>
          </DialogHeader>

          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              save()
            }}
          >
            {editing && kind?.origin === 'builtin' ? (
              <p className="flex gap-2 rounded-xl bg-warn-soft px-3 py-2 text-sm text-ink" role="note">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
                This is one of the studio’s own {words.many}. Changes apply to every picture and story that uses it.
              </p>
            ) : null}
            {editing && detailQ.data ? (
              <p className="text-xs text-ink-3">
                {detailQ.data.usage.count === 0 ? 'Not used on any picture yet.' : `Used on ${detailQ.data.usage.count} picture${detailQ.data.usage.count === 1 ? '' : 's'}.`}
              </p>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <Field label="Name" error={name.length > 0 ? (nameError ?? undefined) : undefined} hint={!editing && suggestion ? `Saved as ${suggestion.id}` : undefined}>
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Name" placeholder={domain === 'region_kind' ? 'e.g. Lava, Sand, Carpet' : domain === 'anchor_kind' ? 'e.g. Hammock, Swing' : domain === 'portal_kind' ? 'e.g. Rabbit hole' : 'e.g. Hop'} maxLength={80} />
              </Field>
              <div className="flex items-end gap-2">
                <span className="inline-flex size-11 items-center justify-center rounded-xl border border-line" style={{ background: effectiveColor }} aria-hidden>
                  <KindIcon name={effectiveIcon} className="size-5 text-ink" />
                </span>
              </div>
            </div>

            {existing ? (
              <div className="flex flex-wrap items-center gap-2 rounded-xl bg-paper-2 px-3 py-2 text-sm" role="status">
                <span className="flex-1">You already have a {words.one} called “{existing.label['en'] ?? existing.id}”.</span>
                <Button type="button" size="sm" variant="secondary" onClick={() => onDone?.(existing)}>
                  Use that one
                </Button>
              </div>
            ) : null}

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-ink">What does it do in stories?</legend>
              {catalogueQ.isError ? (
                <p className="text-xs text-ink-3">The choices could not load — the studio will guess from the name.</p>
              ) : !catalogue ? (
                <p className="text-xs text-ink-3">Loading the choices…</p>
              ) : (
                <div role="group" aria-label="What it does" className="grid gap-1.5 sm:grid-cols-2">
                  {catalogueMeanings.map((m) => {
                    const on = meanings.includes(m.id)
                    return (
                      <button
                        key={m.id}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => setChosen(toggleMeaning(meanings, m.id, catalogueMeanings))}
                        className={cn(
                          'flex items-start gap-2 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:focus-ring',
                          on ? 'border-transparent bg-accent-soft text-ink' : 'border-line bg-paper text-ink-2 hover:bg-paper-2',
                        )}
                      >
                        <span className={cn('mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded border', on ? 'border-accent bg-accent text-accent-ink' : 'border-line')} aria-hidden>
                          {on ? '✓' : ''}
                        </span>
                        <KindIcon name={m.icon} className="mt-0.5 size-4 shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink">{m.label}</span>
                          <span className="block text-xs text-ink-3">{m.hint}</span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
              <div className="rounded-xl bg-paper-2 px-3 py-2 text-sm text-ink-2" aria-live="polite" data-testid="kind-summary">
                <span className="font-medium text-ink">In stories: </span>
                {meanings.length === 0 && (chosen !== null || editing || suggestion) ? `It does nothing yet — it is just a named ${words.one}.` : summary.length ? summary.join(' ') : '…'}
              </div>
            </fieldset>

            <Field label="Colour">
              <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Colour">
                {[...(suggestion?.color && !SWATCHES.includes(suggestion.color) ? [suggestion.color] : []), ...(kind?.color && !SWATCHES.includes(kind.color) ? [kind.color] : []), ...SWATCHES].map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={effectiveColor === c}
                    aria-label={c}
                    onClick={() => setColor(c)}
                    className={cn('size-8 rounded-full border border-line focus-visible:focus-ring', effectiveColor === c && 'ring-2 ring-accent ring-offset-2 ring-offset-paper')}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </Field>

            <div className="flex flex-col gap-2">
              <button type="button" className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink" aria-expanded={iconsOpen} onClick={() => setIconsOpen((v) => !v)}>
                {iconsOpen ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
                Picture: <KindIcon name={effectiveIcon} className="size-4" /> <span className="text-ink-3">{effectiveIcon ?? 'none'}</span>
              </button>
              {iconsOpen ? (
                <div className="flex flex-col gap-2 rounded-xl border border-line p-2">
                  <Input value={iconQuery} onChange={(e) => setIconQuery(e.target.value)} placeholder="Search pictures (tree, star, boat…)" aria-label="Search pictures" fieldSize="sm" />
                  <div className="grid max-h-40 grid-cols-[repeat(auto-fill,minmax(2.5rem,1fr))] gap-1 overflow-y-auto" role="radiogroup" aria-label="Picture">
                    {shownIcons.map((n) => (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={effectiveIcon === n}
                        aria-label={n}
                        title={n}
                        onClick={() => setIcon(n)}
                        className={cn('inline-flex size-10 items-center justify-center rounded-lg hover:bg-paper-2 focus-visible:focus-ring', effectiveIcon === n && 'bg-accent-soft')}
                      >
                        <KindIcon name={n} className="size-5" />
                      </button>
                    ))}
                    {shownIcons.length === 0 ? <p className="col-span-full p-2 text-xs text-ink-3">No picture by that name.</p> : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <button type="button" className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
                {moreOpen ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
                More options{drafts.length ? ` (${drafts.length} extra)` : ''}
              </button>
              {moreOpen ? (
                <div className="flex flex-col gap-3 rounded-xl border border-line p-3">
                  <Field label="What it is (optional)" hint="A sentence people see — and the computer reads when it marks pictures.">
                    <Input value={description} onChange={(e) => setDescription(e.target.value)} aria-label="What it is" />
                  </Field>
                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-medium text-ink">Extra things to fill in</legend>
                    <p className="text-xs text-ink-3">Shown in Details whenever one of these is picked — for example “Colour of the flowers”.</p>
                    {drafts.map((d, i) => (
                      <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_10rem_auto]">
                        <Field label={`Name ${i + 1}`}>
                          <Input
                            value={d.label}
                            onChange={(e) => setDrafts((all) => all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                            aria-label={`Extra thing ${i + 1} name`}
                            fieldSize="sm"
                          />
                        </Field>
                        <Field label="Kind of answer">
                          <select
                            value={d.type}
                            onChange={(e) => setDrafts((all) => all.map((x, j) => (j === i ? { ...x, type: e.target.value as FieldType } : x)))}
                            aria-label={`Extra thing ${i + 1} answer`}
                            className="h-9 rounded-lg border border-line bg-paper px-2 text-sm text-ink focus-visible:focus-ring"
                          >
                            {(Object.keys(TYPE_LABEL) as FieldType[])
                              .filter((t) => t !== 'asset_ref' || d.type === 'asset_ref')
                              .map((t) => (
                                <option key={t} value={t}>
                                  {TYPE_LABEL[t]}
                                </option>
                              ))}
                          </select>
                        </Field>
                        <Button type="button" variant="ghost" size="icon" aria-label={`Remove extra thing ${i + 1}`} onClick={() => setDrafts((all) => all.filter((_, j) => j !== i))}>
                          <Trash2 aria-hidden />
                        </Button>
                        {d.type === 'select' ? (
                          <Field label="Choices (separate with commas)" className="sm:col-span-3">
                            <Input
                              value={d.options}
                              onChange={(e) => setDrafts((all) => all.map((x, j) => (j === i ? { ...x, options: e.target.value } : x)))}
                              aria-label={`Extra thing ${i + 1} choices`}
                              placeholder="red, yellow, blue"
                              fieldSize="sm"
                            />
                          </Field>
                        ) : null}
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setDrafts((all) => [...all, { key: '', label: '', type: 'text', options: '' }])}>
                      <Plus aria-hidden /> Add an extra thing
                    </Button>
                    {fieldsResult.error && drafts.length ? (
                      <p className="text-xs text-danger" role="alert">
                        {fieldsResult.error}
                      </p>
                    ) : null}
                  </fieldset>
                  {editing && kind ? (
                    <Button type="button" variant="link" size="sm" className="w-fit" onClick={() => setFullEditor(true)}>
                      All settings (for experts)…
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {editing && kind ? (
              <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
                {kind.enabled ? (
                  confirmHide ? (
                    <>
                      <span className="flex-1 text-sm text-ink-2">
                        Hide it? It stays on pictures that already use it, but it won’t be offered any more and the computer stops suggesting it.
                      </span>
                      <Button type="button" size="sm" variant="danger" onClick={() => setHidden(true)} loading={patch.isPending}>
                        Hide it
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmHide(false)}>
                        Keep it
                      </Button>
                    </>
                  ) : (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmHide(true)}>
                      <EyeOff aria-hidden /> Hide from the lists
                    </Button>
                  )
                ) : (
                  <Button type="button" size="sm" variant="secondary" onClick={() => setHidden(false)}>
                    <Eye aria-hidden /> Show it in the lists again
                  </Button>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={pending} disabled={Boolean(nameError || fieldsResult.error)}>
                {editing ? 'Save' : `Add ${words.one}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {editing && kind && fullEditor ? <KindEditorDialog open={fullEditor} onOpenChange={setFullEditor} domain={domain} kind={kind} /> : null}
    </>
  )
}
